import axios from 'axios'
import { h } from 'vue'
import { RouterView } from 'vue-router'
import { hashToObject, isAuthenticated } from '@/helpers/helpers'
import LiveCaller from '../LiveCaller'
// Error pages are already eagerly bundled by bootstrap.js (for the $_error
// runtime resolver), so lazy-importing them here is ineffective — import them
// statically to drop the build warning and reflect that they're in the main chunk.
import Error403 from '@/views/pages/errors/Error403'
import Error404 from '@/views/pages/errors/Error404'
import Error419 from '@/views/pages/errors/Error419'
import Error500 from '@/views/pages/errors/Error500'
import Loader from '@/views/pages/errors/Loader'

const MainLayout = () => import('@/views/layouts/MainLayout')
const Dashboard = () => import('@/modules/dashboard-classic/DashboardClassicPage')
const Customer = () => import('@/modules/customer-profile/CustomerProfilePage')
const VisitorsPage = () => import('@/modules/visitors/VisitorsPage')
const BlockedVisitorsModule = () => import('@/modules/blocked-visitors/BlockedVisitorsPage')
const PersonalDetails = () => import('@/modules/profile/PersonalDetailsPage')
const Widgets = () => import('@/modules/widgets/WidgetsListPage')
const Integrations = () => import('@/modules/integrations/IntegrationsPage')
const EditWidget = () => import('@/modules/widgets/WidgetEditPage')
const Analytics = () => import('@/modules/reports/analytics/AnalyticsPage')
const LiveConversationsModule = () => import('@/modules/live-conversations/LiveConversationsPage')
const Billing = () => import('@/modules/billing/BillingPage')
const UpgradePlan = () => import('@/modules/billing/sections/UpgradePlanPage')
const Subscription = () => import('@/modules/billing/sections/SubscriptionSection')
const OrderHistory = () => import('@/modules/billing/sections/OrderHistorySection')
const PaymentMethods = () => import('@/modules/billing/sections/PaymentMethodsSection')
const UserReportsPage = () => import('@/modules/reports/user-reports/UserReportsPage')
const AvailabilityPage = () => import('@/modules/reports/availability/AvailabilityPage')
const TriggersModule = () => import('@/modules/triggers/TriggersPage')
const Notifications = () => import('@/modules/profile/NotificationsPage')
const ConversationReports = () => import('@/modules/reports/conversation-reports/ConversationReports')
const SecuritySettings = () => import('@/modules/users/settings/security/views/SecuritySettings')
const UserSignIn = () => import('@/modules/auth/signin/UserSignIn')
const AccountSignUp = () => import('@/modules/auth/signup/AccountSignUp')
const ForgotPassword = () => import('@/modules/auth/password/ForgotPassword')
const ResetPassword = () => import('@/modules/auth/password/ResetPassword')
const OAuthCallback = () => import('@/modules/auth/oauth-callback')

// Guest route guard - redirects authenticated users
function guestGuard(to, from, next) {
    if (isAuthenticated()) {
        next('/')
    } else {
        next()
    }
}

// PBX Components
const PbxQueues = () => import('@/modules/pbx/queues/PbxQueue.vue')
const PbxTrunks = () => import('@/modules/pbx/trunks/PbxTrunk.vue')
const PbxEndpoints = () => import('@/modules/pbx/endpoints/PbxEndpoint.vue')
const AnalyticsHubPage = () => import('@/modules/analytics/AnalyticsHubPage.vue')
const KnowledgePage = () => import('@/modules/knowledge/KnowledgePage.vue')
const PhonePage = () => import('@/modules/phone/PhonePage.vue')
const AiPage = () => import('@/modules/ai/AiPage.vue')
const PbxFlows = () => import('@/modules/pbx/flows/PbxFlow.vue')
const PbxFlowBuilder = () => import('@/modules/pbx/flows/flow-builder/PbxFlowBuilderPage.vue')
const ChatbotList = () => import('@/modules/chatbot/ChatbotListPage.vue')
const ChatbotBuilder = () => import('@/modules/chatbot/ChatbotBuilderPage.vue')

// AI Components
const AiWorkspaces = () => import('@/modules/ai/workspaces/AiWorkspaces.vue')
const AiAssistants = () => import('@/modules/ai/assistants/AiAssistants.vue')
const AiInteractions = () => import('@/modules/ai/interactions/AiInteractions.vue')
const AiInteractionDetail = () => import('@/modules/ai/interactions/AiInteractionDetail.vue')
const AiVectorStores = () => import('@/modules/ai/vector-stores/AiVectorStores.vue')
const AiSources = () => import('@/modules/ai/sources/AiSources.vue')

// Dashboard Module (Vue 2.7 Composition API)
const DashboardModule = () => import('@/modules/dashboard/Dashboard')

// New Module Components (Vue 2.7 Composition API with DataGrid)
const UsersModule = () => import('@/modules/users/users-page/UsersPage')
const CallbacksModule = () => import('@/modules/callbacks/CallbacksPage')
const ConversationHistoryModule = () => import('@/modules/conversation-history/ConversationHistory')
const InboxModule = () => import('@/modules/inbox/InboxPage')
const RolesModule = () => import('@/modules/roles/RolesPage')
const ShortcutsModule = () => import('@/modules/shortcuts/ShortcutsPage')
const TagsModule = () => import('@/modules/tags/TagsPage')
const DepartmentsModule = () => import('@/modules/departments/DepartmentsPage')
const WorkingHoursModule = () => import('@/modules/working-hours/WorkingHoursPage')
const KnowledgeBasesModule = () => import('@/modules/knowledge-base/kb-pages/KbListPage')
const KbWorkspace = () => import('@/modules/knowledge-base/kb-workspace/KbWorkspace')
const SettingsPage = () => import('@/modules/settings/SettingsPage')
const AwayReasonsSettings = () => import('@/modules/settings/away-reasons/AwayReasonsSettings')

export default [
    {
        path: '/',
        component: MainLayout,
        meta: { middleware: ['auth'] },
        children: [
            {
                path: '',
                component: {
                    render: () => h(RouterView),
                },
                meta: { middleware: ['subscription'] },
                children: [
                    {
                        name: 'dashboard',
                        path: '',
                        component: Dashboard,
                    },
                    {
                        name: 'dashboard-module',
                        path: 'dashboard',
                        component: DashboardModule,
                        meta: {
                            title: 'Dashboard (New)',
                            requiresAuth: true,
                        },
                    },
                    {
                        name: 'users.index',
                        path: 'users',
                        redirect: { name: 'settings.users' },
                    },
                    {
                        name: 'roles.index',
                        path: 'roles',
                        redirect: { name: 'settings.roles' },
                    },
                    {
                        name: 'customers.index',
                        path: 'customers',
                        component: VisitorsPage,
                        meta: { requiresAuth: true, title: 'Customers' },
                    },
                    {
                        name: 'customers.show',
                        path: 'customers/:id',
                        component: Customer,
                    },
                    {
                        name: 'blocked-visitors',
                        path: 'blocked-visitors',
                        component: BlockedVisitorsModule,
                    },
                    {
                        name: 'personal-details',
                        path: 'personal-details',
                        component: PersonalDetails,
                    },
                    {
                        name: 'departments',
                        path: 'departments',
                        redirect: { name: 'settings.departments' },
                    },
                    {
                        name: 'conversations',
                        path: 'conversations',
                        component: LiveConversationsModule,
                    },
                    {
                        name: 'working-hours',
                        path: 'working-hours',
                        redirect: { name: 'settings.working-hours' },
                    },
                    {
                        name: 'widgets.index',
                        path: 'widgets',
                        redirect: { name: 'settings.widgets' },
                    },
                    {
                        name: 'widget.edit',
                        path: 'widget/:id/edit',
                        component: EditWidget,
                    },
                    {
                        name: 'integrations.index',
                        path: 'integrations',
                        redirect: { name: 'settings.integrations' },
                    },
                    // Analytics — sub-nav page (General/User Report/Conversation Reports/Availability)
                    {
                        name: 'analytics-hub',
                        path: 'analytics',
                        component: AnalyticsHubPage,
                        redirect: { name: 'analytics' },
                        meta: { requiresAuth: true, title: 'Analytics' },
                        children: [
                            { name: 'analytics', path: 'general', component: Analytics, meta: { title: 'General Analytics' } },
                            { name: 'user-reports', path: 'reports', component: UserReportsPage, meta: { title: 'User Reports' } },
                            { name: 'user-reports.detail', path: 'reports/:id', component: UserReportsPage, meta: { title: 'User Report Details' } },
                            { path: 'conversation-reports', redirect: { name: 'conversation-reports' } },
                            { name: 'availability', path: 'availability', component: AvailabilityPage, meta: { title: 'Availability' } },
                            { name: 'availability.detail', path: 'availability/:userId', component: AvailabilityPage, meta: { title: 'User Availability' } },
                        ],
                    },
                    // legacy path redirects
                    { path: '/reports', redirect: { name: 'user-reports' } },
                    { path: '/reports/:id', redirect: (to) => ({ name: 'user-reports.detail', params: to.params }) },
                    { path: '/availability', redirect: { name: 'availability' } },
                    { path: '/availability/:userId', redirect: (to) => ({ name: 'availability.detail', params: to.params }) },
                    {
                        name: 'shortcuts',
                        path: 'shortcuts',
                        redirect: { name: 'settings.shortcuts' },
                    },
                    {
                        name: 'tags',
                        path: 'tags',
                        redirect: { name: 'settings.tags' },
                    },
                    {
                        name: 'callbacks',
                        path: '/callbacks',
                        component: CallbacksModule,
                        meta: { requiresAuth: true, title: 'Callbacks' },
                    },
                    {
                        name: 'triggers',
                        path: '/triggers',
                        redirect: { name: 'settings.rules' },
                    },
                    // rule create/edit live as settings children (settings.rules tab)
                    { path: '/triggers/create', redirect: { name: 'triggers.create' } },
                    { path: '/triggers/edit/:id', redirect: (to) => ({ name: 'triggers.edit', params: to.params }) },
                    {
                        name: 'notifications.index',
                        path: '/notifications',
                        component: Notifications,
                    },
                    {
                        name: 'conversation-reports',
                        path: '/conversation-reports',
                        component: ConversationReports,
                        meta: { requiresAuth: true, title: 'Conversation Reports' },
                    },
                    // Phone (PBX) — sub-nav page wrapping the feature pages
                    {
                        name: 'phone',
                        path: '/phone',
                        component: PhonePage,
                        redirect: { name: 'pbx.trunks' },
                        meta: { requiresAuth: true, title: 'Phone' },
                        children: [
                            { name: 'pbx.trunks', path: 'numbers', component: PbxTrunks, meta: { title: 'Numbers' } },
                            { name: 'pbx.endpoints', path: 'devices', component: PbxEndpoints, meta: { title: 'Devices' } },
                            { name: 'pbx.queues', path: 'queues', component: PbxQueues, meta: { title: 'Queues' } },
                            { name: 'pbx.flows', path: 'flows', component: PbxFlows, meta: { title: 'Flows' } },
                        ],
                    },
                    // full-screen builder stays outside the sub-nav layout
                    {
                        name: 'pbx.flows.builder',
                        path: '/pbx/flows/:id/builder',
                        component: PbxFlowBuilder,
                    },
                    // legacy path redirects
                    { path: '/pbx/trunks', redirect: { name: 'pbx.trunks' } },
                    { path: '/pbx/endpoints', redirect: { name: 'pbx.endpoints' } },
                    { path: '/pbx/queues', redirect: { name: 'pbx.queues' } },
                    { path: '/pbx/flows', redirect: { name: 'pbx.flows' } },
                    // chatbot list lives as a settings child (Chatbot tab);
                    // the builder stays full-page — the flow canvas needs the room
                    { path: '/chatbots', redirect: { name: 'chatbots' } },
                    {
                        name: 'chatbot',
                        path: '/chatbots/:id',
                        component: ChatbotBuilder,
                    },
                    // AI — sub-nav page wrapping the feature pages
                    {
                        name: 'ai',
                        path: '/ai',
                        component: AiPage,
                        redirect: { name: 'ai.workspaces' },
                        meta: { requiresAuth: true, title: 'AI' },
                        children: [
                            { name: 'ai.workspaces', path: 'workspaces', component: AiWorkspaces, meta: { title: 'Workspaces' } },
                            { name: 'ai.vector-stores', path: 'vector-stores', component: AiVectorStores, meta: { title: 'Vector Stores' } },
                            { name: 'ai.sources', path: 'vector-stores/:id/sources', component: AiSources, meta: { title: 'Vector Store Sources' } },
                            { name: 'ai.assistants', path: 'assistants', component: AiAssistants, meta: { title: 'Assistants' } },
                            { name: 'ai.interactions', path: 'interactions', component: AiInteractions, meta: { title: 'Interactions' } },
                            { name: 'ai.interaction-detail', path: 'interactions/:id', component: AiInteractionDetail, meta: { title: 'Interaction Details' } },
                        ],
                    },
                    // New Module Routes (DataGrid-based)
                    {
                        name: 'conversation-history',
                        path: '/conversation-history',
                        component: ConversationHistoryModule,
                        meta: {
                            requiresAuth: true,
                            title: 'Conversation History',
                        },
                    },
                    {
                        name: 'inbox',
                        path: '/inbox',
                        component: InboxModule,
                        meta: { requiresAuth: true, title: 'Inbox' },
                    },
                    {
                        name: 'inbox-show',
                        path: '/inbox/:visitorId/:conversationId',
                        component: InboxModule,
                        meta: { requiresAuth: true, title: 'Inbox' },
                    },
                    {
                        name: 'inbox-visitor',
                        path: '/inbox/:visitorId',
                        component: InboxModule,
                        meta: { requiresAuth: true, title: 'Inbox' },
                    },
                    // Knowledge — sub-nav page
                    {
                        name: 'knowledge',
                        path: '/knowledge',
                        component: KnowledgePage,
                        redirect: { name: 'knowledge-bases' },
                        meta: { requiresAuth: true, title: 'Knowledge' },
                        children: [
                            { name: 'knowledge-bases', path: 'bases', component: KnowledgeBasesModule, meta: { title: 'Knowledge Bases' } },
                            { name: 'knowledge-base-workspace', path: 'bases/:id/:articleId?', component: KbWorkspace, meta: { title: 'Knowledge Base' } },
                        ],
                    },
                    { path: '/kbs', redirect: { name: 'knowledge-bases' } },
                    { path: '/kbs/:id/:articleId?', redirect: (to) => ({ name: 'knowledge-base-workspace', params: to.params }) },
                    {
                        name: 'security',
                        path: 'security',
                        component: SecuritySettings,
                    },
                    {
                        // Settings shell: a tabbed page that reuses the existing
                        // feature pages as tabs (see SettingsPage.vue).
                        name: 'settings',
                        path: '/settings',
                        component: SettingsPage,
                        redirect: { name: 'settings.users' },
                        meta: {
                            requiresAuth: true,
                            title: 'Settings',
                        },
                        children: [
                            { name: 'settings.users', path: 'users', component: UsersModule, meta: { title: 'Users' } },
                            { name: 'settings.roles', path: 'roles', component: RolesModule, meta: { title: 'Roles' } },
                            { name: 'settings.departments', path: 'departments', component: DepartmentsModule, meta: { title: 'Departments' } },
                            { name: 'settings.widgets', path: 'widgets', component: Widgets, meta: { title: 'Widgets' } },
                            { name: 'settings.away-reasons', path: 'away-reasons', component: AwayReasonsSettings, meta: { title: 'Away Reasons' } },
                            { name: 'settings.rules', path: 'rules', component: TriggersModule, meta: { title: 'Rules' } },
                            { name: 'triggers.create', path: 'rules/create', component: TriggersModule, meta: { title: 'Create Rule' } },
                            { name: 'triggers.edit', path: 'rules/edit/:id', component: TriggersModule, meta: { title: 'Edit Rule' } },
                            { name: 'chatbots', path: 'chatbots', component: ChatbotList, meta: { title: 'Chatbot' } },
                            { name: 'settings.working-hours', path: 'working-hours', component: WorkingHoursModule, meta: { title: 'Working Hours' } },
                            { name: 'settings.integrations', path: 'integrations', component: Integrations, meta: { title: 'Integrations' } },
                            { name: 'settings.shortcuts', path: 'shortcuts', component: ShortcutsModule, meta: { title: 'Chat shortcuts' } },
                            { name: 'settings.tags', path: 'tags', component: TagsModule, meta: { title: 'Tags' } },
                            {
                                path: 'billing',
                                component: Billing,
                                redirect: { name: 'settings.billing.subscription' },
                                // Reachable while unsubscribed — this is where an
                                // unsubscribed account goes to subscribe, so the
                                // subscription guard must let it (and its children)
                                // through instead of redirecting back here.
                                meta: { skipSubscription: true },
                                children: [
                                    { name: 'settings.billing.subscription', path: 'subscription', component: Subscription },
                                    { name: 'settings.billing.upgrade', path: 'upgrade', component: UpgradePlan },
                                    { name: 'settings.billing.history', path: 'history', component: OrderHistory },
                                    { name: 'settings.billing.payment', path: 'payment-methods', component: PaymentMethods },
                                ],
                            },
                        ],
                    },
                ],
            },
            // Billing now lives under the Settings shell (/settings/billing/*).
            // The old top-level routes redirect there so links/nav stay consistent.
            { name: 'billing', path: 'billing', redirect: { name: 'settings.billing.subscription' } },
            { name: 'billing.subscription', path: 'billing/subscription', redirect: { name: 'settings.billing.subscription' } },
            { name: 'billing.upgrade', path: 'billing/upgrade', redirect: { name: 'settings.billing.upgrade' } },
            { name: 'billing.history', path: 'billing/history', redirect: { name: 'settings.billing.history' } },
            { name: 'billing.payment', path: 'billing/payment-methods', redirect: { name: 'settings.billing.payment' } },
        ],
    },
    {
        name: 'auth.callback',
        path: '/auth/callback',
        component: Loader,
        beforeEnter: (to, from, next) => {
            const hash = hashToObject(to.hash)

            if (hash.hasOwnProperty('impersonate')) {
                const { token_type, expires_in, access_token } = hash
                LiveCaller.app.$storage.set('oauth', {
                    token_type,
                    expires_in,
                    access_token,
                })
                window.location = LiveCaller.config.get('app.baseURL')
                return
            }

            const urlParams = new URLSearchParams(window.location.search)
            const code = urlParams.get('code')
            const state = urlParams.get('state')
            const oAuthState = LiveCaller.app.$storage.get('oauth_state', {})

            if (code && state && state === oAuthState.state) {
                const authConfig = LiveCaller.config.get('auth')
                const params = {
                    grant_type: 'authorization_code',
                    client_id: authConfig.client_id,
                    redirect_uri: authConfig.redirect_uri,
                    code_verifier: oAuthState.verifier,
                    code,
                }
                axios.post(`${authConfig.baseURL}/token`, params)
                    .then((resp) => {
                        const { token_type, expires_in, access_token, refresh_token } = resp.data
                        LiveCaller.app.$storage.remove('oauth')
                        LiveCaller.app.$storage.remove('oauth_state')
                        LiveCaller.app.$storage.set('oauth', {
                            token_type,
                            expires_in,
                            access_token,
                            refresh_token,
                        })
                        window.location = LiveCaller.config.get('app.baseURL')
                    })
                    .catch(() => next('/500'))
            }
        },
    },
    {
        path: '/login',
        redirect: '/signin',
    },
    {
        name: 'signin',
        path: '/signin',
        component: UserSignIn,
        beforeEnter: guestGuard,
    },
    {
        name: 'signup',
        path: '/signup',
        component: AccountSignUp,
        beforeEnter: guestGuard,
    },
    {
        name: 'forgot-password',
        path: '/forgot-password',
        component: ForgotPassword,
        beforeEnter: guestGuard,
    },
    {
        name: 'reset-password',
        path: '/reset-password/:token',
        component: ResetPassword,
        beforeEnter: guestGuard,
    },
    {
        name: '403',
        path: '/403',
        component: Error403,
    },
    {
        name: '404',
        path: '/404',
        component: Error404,
    },
    {
        name: '419',
        path: '/419',
        component: Error419,
    },
    {
        name: '500',
        path: '/500',
        component: Error500,
    },
    {
        name: 'catch-all',
        path: '/:pathMatch(.*)*',
        component: Error404,
    },
    {
        path: '/oauth/callback',
        name: 'oauth-callback',
        component: OAuthCallback,
        meta: {
            guest: true,
        },
    },
]

